import React, { useEffect, useState, useRef } from "react";
import styled from "styled-components";
import { CssBaseline } from "@mui/material";
import { useTestTargets } from "../hooks/useTestTargets";
import { TestTarget } from "../types/TestTarget";
import { useTestcaseMappings } from "../hooks/useTestcaseMappings";
import ServiceList from "./ServiceList";
import TestTargetGraphView from "./TestTargetGraphView";
import TestTargetDetailView from "./TestTargetDetailView";
import { TestTargetType, TestTargetFormData } from "../types/TestTarget";
import { SSTFApiClient } from "../services/api";
import { getTypeIdFromTypeName } from "../services/testTargetService";
import { ApiConfig } from "../config/apiConfig";
import { isGenerationResultExportImportEnabled } from "../config/featureFlags";
import { OperationLogImportDialog } from "./OperationLogImportDialog";
import { TestTargetRegistrationDialog } from "./TestTargetRegistrationDialog";
import { TransitionGraphDialog } from "./transitionGraphDialog";
import { TransitionGraphCreateDialog } from "./transitionGraphCreateDialog";
import { GroupPreviewDialog, PathSelection } from "./GroupPreviewDialog";
import {
  OperationLogService,
  GenerateFromOperationLogResponse,
  OperationLogPreview,
  GenerateFromOperationLogRequest,
  RegisterTestTargetsRequest,
  RegisterTestTargetsResponse,
} from "../services/operationLogService";
import { registerSpecImportTargets } from "../services/spec-import-registration";
import type { DeletionCandidate, SpecImportSummary } from "../utils/spec-import/types";
import {
  TransitionGraphService,
  transitionGraphService,
} from "../services/transitionGraphService";
import { useToast } from "../contexts/ToastContext";
import { toCreatedTestTarget } from "../utils/testTargetMapper";

const DEFAULT_DISPLAY_ORDER = 1;

// Styled Components
const AppContainer = styled.div`
  display: flex;
  height: 100vh;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
  background-color: #f5f6fa;
`;

const Sidebar = styled.div`
  width: 300px;
  background: white;
  box-shadow: 4px 0 20px rgba(0, 0, 0, 0.08);
  z-index: 1200;
  border-right: 1px solid rgba(0, 0, 0, 0.06);
  flex-shrink: 0;
`;

const MainContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
  background-color: #f5f6fa;
`;

const LoadingContent = styled.div`
  text-align: center;
  padding: 40px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid #e3f2fd;
  border-top: 4px solid #1976d2;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.p`
  margin: 0;
  color: #666;
  font-size: 1rem;
`;

const ErrorContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
  background-color: #f5f6fa;
`;

const ErrorContent = styled.div`
  text-align: center;
  padding: 40px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  border-left: 4px solid #f44336;
`;

const ErrorIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 16px;
`;

const ErrorText = styled.p`
  margin: 0;
  color: #d32f2f;
  font-size: 1.1rem;
  font-weight: 600;
`;

interface TestTargetManagerProps {
  apiConfig: ApiConfig;
  apiClient: SSTFApiClient | null;
  onApiConfigChange: (config: ApiConfig) => void;
  onApiClientChange: (client: SSTFApiClient | null) => void;
  workspaceId: string;
  projectId: string;
  workspaceName?: string;
  projectName?: string;
  onWorkspaceIdChange: (workspaceId: string) => void;
  onProjectIdChange: (projectId: string) => void;
  showApiConfigDialog?: boolean;
  onApiConfigComplete?: () => void;
  // URLパラメータによるダイアログ制御
  urlTargetId?: string;
  urlUsecaseId?: string;
  urlEditMode?: boolean;
  onCloseDialog?: () => void;
}

const TestTargetManager: React.FC<TestTargetManagerProps> = ({
  apiConfig,
  apiClient,
  onApiConfigChange,
  onApiClientChange,
  workspaceId,
  projectId,
  workspaceName,
  projectName,
  onWorkspaceIdChange,
  onProjectIdChange,
  showApiConfigDialog = false,
  onApiConfigComplete,
  urlTargetId,
  urlUsecaseId,
  urlEditMode,
  onCloseDialog,
}) => {
  const { showSuccess, showError } = useToast();
  const [selectedServiceIds, setSelectedServiceIds] = useState<Set<string>>(
    new Set()
  );
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formData, setFormData] = useState<TestTargetFormData | null>(null);
  const [isCreateMode, setIsCreateMode] = useState(false);
  const [selectedViewForItems, setSelectedViewForItems] =
    useState<TestTarget | null>(null);
  // View 選択の発生源。サイドバーのリスト選択のときだけ全体ビューをパン・ハイライトするため、
  // グラフ上のノードクリックやパネル内ナビと区別する（issue #342 要望2）。
  const [selectedViewSource, setSelectedViewSource] = useState<
    "graph" | "panel" | "sidebar" | null
  >(null);
  const prevWorkspaceIdRef = useRef<string | null>(null);
  const prevProjectIdRef = useRef<string | null>(null);
  const generationResultFileInputRef = useRef<HTMLInputElement>(null);

  // 操作ログインポート関連の状態
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [showRegistrationDialog, setShowRegistrationDialog] = useState(false);
  const [generatedResult, setGeneratedResult] = useState<GenerateFromOperationLogResponse | null>(null);
  const [pendingResult, setPendingResult] = useState<GenerateFromOperationLogResponse | null>(null);
  const [pendingGenerateRequest, setPendingGenerateRequest] = useState<GenerateFromOperationLogRequest | null>(null);
  const [previewData, setPreviewData] = useState<OperationLogPreview | null>(null);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [pathUpdateInProgress, setPathUpdateInProgress] = useState(false);
  // 空文字列を指定すると相対パスになり、proxyサーバー経由でリクエストされる
  const [operationLogService] = useState(() => new OperationLogService(""));

  // 仕様書インポートダイアログの開閉状態（ServiceList の内部状態を外部から制御する用途）
  const [showSpecImportDialog, setShowSpecImportDialog] = useState(false);

  // 仕様書インポート専用: registerTestTargets を SSTF GraphQL に直接送る custom handler
  // 値が null のときは TestTargetRegistrationDialog は通常の operationLogService 経路を使う
  const [specCustomRegisterHandler, setSpecCustomRegisterHandler] = useState<
    | ((
        request: RegisterTestTargetsRequest & {
          deleteCandidateIds?: Set<string>;
          existingChildrenOf?: Record<string, string[]>;
        },
      ) => Promise<RegisterTestTargetsResponse>)
    | null
  >(null);

  // 仕様書インポート専用: 削除候補と既存ターゲットの親子マップ
  const [specDeletionCandidates, setSpecDeletionCandidates] = useState<DeletionCandidate[] | undefined>(undefined);
  const [specExistingChildrenOf, setSpecExistingChildrenOf] = useState<Record<string, string[]> | undefined>(undefined);

  // 遷移情報一括登録関連の状態
  const [showTransitionGraphDialog, setShowTransitionGraphDialog] = useState(false);
  const [showTransitionGraphCreateDialog, setShowTransitionGraphCreateDialog] = useState(false);

  // 操作ログ処理のプログレス状態
  const [isProcessingOperationLog, setIsProcessingOperationLog] =
    useState(false);
  const [processingMessage, setProcessingMessage] = useState("");
  
  // ServiceListの幅を管理
  const [serviceListWidth, setServiceListWidth] = useState(300);

  // TestTargetDetailViewの初期タブ
  const [detailViewInitialTab, setDetailViewInitialTab] = useState<string>("basic");

  // ServiceListからService詳細を開くリクエスト（TestTargetGraphViewの詳細ダイアログを開く）
  const [requestedServiceForDetail, setRequestedServiceForDetail] = useState<TestTarget | null>(null);

  // 認証エラー時にAPIクライアントをリセットする関数
  const handleAuthError = () => {
    onApiClientChange(null); // APIクライアントをリセット
  };

  // View選択ハンドラー
  // source 既定は "sidebar"（サイドバーのリスト選択）。グラフ/パネル由来の呼び出しは
  // 明示的に source を渡してフォーカス対象外にする。
  const handleViewSelect = (
    view: TestTarget,
    source: "graph" | "panel" | "sidebar" = "sidebar"
  ) => {
    setSelectedViewForItems(view);
    setSelectedViewSource(source);
  };

  // 項目仕様書パネルを閉じたときのハンドラー
  const handleViewPanelClose = () => {
    // NOTE: selectedViewForItemsをリセットすることで、同じViewを再度クリックしたときに
    // useEffectが再実行され、パネルが開くようになる
    setSelectedViewForItems(null);
    setSelectedViewSource(null);
  };

  const {
    testTargets,
    loading,
    error,
    fetchTestTargets,
    updateTestTarget,
    removeTestTarget,
    addTestTarget,
  } = useTestTargets({
    apiClient,
    workspaceId,
    projectId,
    onAuthError: handleAuthError,
  });

  const {
    testcaseMappings,
    loading: testcaseMappingsLoading,
    error: testcaseMappingsError,
    fetchTestcaseMappings,
  } = useTestcaseMappings({
    apiClient,
    workspaceId,
    projectId,
    onAuthError: handleAuthError,
  });

  // useTestTargetsフックが自動的にAPI設定変更を検知してデータを取得するため、
  // 手動でのfetchTestTargets呼び出しは不要

  // API設定の整合性チェック（不足時は console.warn）
  useEffect(() => {
    // API設定が正しく設定されているかチェック
    if (apiConfig.isEnabled && !apiClient) {
      console.warn(
        "⚠️ API設定が有効だが、APIクライアントが初期化されていません"
      );
    }
    if (apiClient && (!workspaceId || !projectId)) {
      console.warn(
        "⚠️ APIクライアントは存在するが、ワークスペースIDまたはプロジェクトIDが設定されていません"
      );
    }
  }, [testTargets, apiClient, workspaceId, projectId, apiConfig]);

  // NOTE: workspaceIdまたはprojectIdが変更されたときにselectedViewForItemsをリセット
  // NOTE: 依存サイクルを避けるため、selectedViewForItemsを依存配列から除外
  useEffect(() => {
    const prevWorkspaceId = prevWorkspaceIdRef.current;
    const prevProjectId = prevProjectIdRef.current;
    if (prevWorkspaceId !== workspaceId || prevProjectId !== projectId) {
      prevWorkspaceIdRef.current = workspaceId || null;
      prevProjectIdRef.current = projectId || null;
      // NOTE: workspaceId/projectIdが変更された場合は常にリセット
      setSelectedViewForItems(null);
    }
  }, [workspaceId, projectId]);

  // サービス一覧を抽出してdisplayOrderでソート
  const services = testTargets
    .filter((target: TestTarget) => target.type === "service")
    .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));

  // サービスが読み込まれたらすべて選択（未選択のときのみ）
  useEffect(() => {
    if (services.length > 0 && selectedServiceIds.size === 0) {
      setSelectedServiceIds(new Set(services.map((s) => s.id)));
    }
  }, [services, selectedServiceIds]);

  // 親の変更処理
  const handleMoveTarget = async (targetId: string, newParentId: string) => {
    // TODO: APIコールでデータベースを更新
    // 現在はモックデータなので、実際の実装では API を呼び出す
    await fetchTestTargets(); // データを再取得
  };

  // 子要素の追加処理
  const handleAddChild = (parentId: string, type: TestTargetType) => {
    setDetailViewInitialTab("basic");
    setFormData({
      name: "",
      description: "",
      type: type,
      displayOrder: DEFAULT_DISPLAY_ORDER,
      parentId: parentId,
      status: "draft",
      params: {},
    });
    setIsCreateMode(true);
    setIsFormOpen(true);
  };

  /** 左サイドバーからルート「サービス」を新規作成 */
  const handleCreateService = () => {
    if (!apiConfig.isEnabled || !apiClient || !workspaceId || !projectId) {
      showError(
        "API を有効にし、ワークスペース ID とプロジェクト ID を設定してください。"
      );
      return;
    }
    setFormData({
      name: "",
      description: "",
      type: "service",
      displayOrder: DEFAULT_DISPLAY_ORDER,
      parentId: undefined,
      status: "draft",
      params: {},
    });
    setIsCreateMode(true);
    setDetailViewInitialTab("basic");
    setIsFormOpen(true);
  };

  // 削除処理（一括削除時は suppressSuccess: true で呼ばれ、成功トーストは呼び出し元で1回だけ表示する）
  const handleDeleteTarget = async (
    targetId: string,
    options?: { suppressSuccess?: boolean }
  ): Promise<void> => {
    try {
      if (!apiClient || !workspaceId || !projectId) {
        console.warn(
          "API設定またはワークスペース・プロジェクト情報が不足しています。モックデータとして処理します。"
        );
        await fetchTestTargets();
        return;
      }

      await apiClient.deleteTestTarget(workspaceId, projectId, targetId);
      await fetchTestTargets();

      if (!options?.suppressSuccess) {
        showSuccess("テストターゲットを削除しました");
      }
    } catch (error) {
      console.error("❌ TestTarget削除エラー:", error);
      throw error;
    }
  };

  // フォーム送信処理
  const handleFormSubmit = async (testTargetData: TestTargetFormData) => {
    try {
      if (!apiClient || !workspaceId || !projectId) {
        console.warn(
          "API設定またはワークスペース・プロジェクト情報が不足しています。モックデータとして処理します。"
        );

        // 仮の実装: 新しいIDを生成してデータを追加
        const newId = `${Date.now()}`;
        const statusValue = testTargetData.status || "draft";
        const paramsWithStatus = {
          ...(testTargetData.params || {}),
          status: statusValue,
        };
        const newTestTarget: TestTarget = {
          id: newId,
          ...testTargetData,
          params: paramsWithStatus,
          metaInfo: {
            createdAt: new Date().toISOString(),
            createdBy: "system",
            modifiedAt: new Date().toISOString(),
            modifiedBy: "system",
          },
        };
        if (addTestTarget) {
          await addTestTarget(newTestTarget);
        }

        // 作成成功時にダイアログを閉じる
        setIsFormOpen(false);
        setFormData(null);
        setIsCreateMode(false);
        return;
      }

      // 編集モードか新規作成モードかを判定
      // formDataにidがあれば編集モード
      const isEditingExisting = !isCreateMode && formData && 'id' in formData;

      if (isEditingExisting) {
        // 編集モード: updateTestTargetを使用
        const targetId = (formData as any).id;

        await apiClient.updateTestTarget(
          workspaceId,
          projectId,
          targetId,
          {
            name: testTargetData.name,
            type: testTargetData.type,
            description: testTargetData.description,
            displayOrder: testTargetData.displayOrder,
            status: testTargetData.status,
            parentId: testTargetData.parentId,
            params: testTargetData.params,
          }
        );

        // ローカル状態を更新
        const existingTarget = testTargets.find((t) => t.id === targetId);
        if (existingTarget && updateTestTarget) {
          // paramsにstatusを含める
          // NOTE: statusがnullやundefinedの場合は"draft"をデフォルト値として設定
          const statusValue = testTargetData.status || "draft";
          const paramsWithStatus = {
            ...(testTargetData.params || {}),
            status: statusValue,
          };

          const updatedTarget: TestTarget = {
            ...existingTarget,
            name: testTargetData.name,
            type: testTargetData.type,
            description: testTargetData.description || "",
            displayOrder: testTargetData.displayOrder,
            parentId: testTargetData.parentId, // 親要素IDを更新
            params: paramsWithStatus, // params内にstatusを含める
          };
          updateTestTarget(updatedTarget);
        }

        // NOTE: トーストメッセージはTestTargetDetailViewで表示されるため、ここでは表示しない
        setIsFormOpen(false);
        setFormData(null);
        return;
      }

      // 新規作成モード
      // 動的にtypeIdを取得
      const typeId = await getTypeIdFromTypeName(
        testTargetData.type,
        apiClient,
        workspaceId,
        projectId
      );
      if (!typeId) {
        throw new Error(`未知の種別です: ${testTargetData.type}`);
      }

      // 実際のAPI呼び出し（新規作成）
      const createInput = {
        name: testTargetData.name,
        displayOrder: testTargetData.displayOrder,
        typeId: typeId,
        description: testTargetData.description,
        parentTestTargetId: testTargetData.parentId,
        params: JSON.stringify(testTargetData.params || {}),
      };

      const response = await apiClient.createTestTargets(
        workspaceId,
        projectId,
        [createInput]
      );

      // API応答から新しく作成されたアイテムをローカル状態に追加
      if (!response || response.length === 0) {
        throw new Error("TestTargetの作成結果が取得できませんでした");
      }

      const createdTarget = response[0];
      const newTarget = toCreatedTestTarget(createdTarget, testTargetData);
      if (addTestTarget) {
        await addTestTarget(newTarget);
      }

      // 作成成功時にダイアログを閉じる（作成トーストは TestTargetDetailView で1回だけ表示）
      setIsFormOpen(false);
      setFormData(null);
      setIsCreateMode(false);
    } catch (error) {
      console.error("TestTarget作成エラー:", error);
      // NOTE: エラートーストはTestTargetDetailViewで表示されるため、ここでは再スローのみ
      throw error;
    }
  };

  // フォームを閉じる処理
  const handleFormClose = () => {
    setIsFormOpen(false);
    setFormData(null);
    setIsCreateMode(false);
    setDetailViewInitialTab("basic"); // 初期タブをリセット
  };

  // Item詳細を開くハンドラー
  const handleOpenItemDetail = (itemId: string) => {
    // itemIdでItemを検索
    const item = testTargets.find(
      (target) => target.id === itemId && target.type === "item"
    );
    
    if (item) {
      // ItemをformDataに設定（idを含める）
      setFormData({
        ...item,
        name: item.name,
        description: item.description || "",
        type: item.type,
        displayOrder: item.displayOrder || 0,
        parentId: item.parentId,
        status: item.params?.status || "draft",
        params: item.params || {},
      } as TestTargetFormData & { id: string });
      setIsCreateMode(false);
      setDetailViewInitialTab("behaviorDetails");
      setIsFormOpen(true);
    } else {
      console.warn(`Item not found: ${itemId}`);
    }
  };

  // 操作ログインポート関連のハンドラー
  const handleOperationLogImport = () => {
    // 前回の処理状態をリセット
    setIsProcessingOperationLog(false);
    setProcessingMessage("");
    setGeneratedResult(null);
    setPendingResult(null);
    setPreviewData(null);
    setPendingGenerateRequest(null);
    setShowRegistrationDialog(false);
    setShowImportDialog(true);
  };

  /** 保存した生成結果JSONを選択して登録ダイアログを直接表示する */
  const handleLoadGenerationResultJson = () => {
    generationResultFileInputRef.current?.click();
  };

  const handleGenerationResultFileChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const text = reader.result as string;
        const parsed = JSON.parse(text) as GenerateFromOperationLogResponse;
        if (
          !parsed ||
          !Array.isArray(parsed.page_results)
        ) {
          showError("無効な形式です。page_results を含むJSONを指定してください。");
          return;
        }
        setGeneratedResult(parsed);
        setShowRegistrationDialog(true);
      } catch {
        showError("JSONの解析に失敗しました。");
      }
    };
    reader.readAsText(file, "UTF-8");
    e.target.value = "";
  };

  // 遷移情報一括登録関連のハンドラー
  const handleTransitionGraphImport = () => {
    setShowTransitionGraphDialog(true);
  };

  const handleTransitionGraphDialogClose = () => {
    setShowTransitionGraphDialog(false);
  };

  // 操作ログから遷移情報のGraph作成関連のハンドラー
  const handleTransitionGraphCreate = () => {
    setShowTransitionGraphCreateDialog(true);
  };

  const handleTransitionGraphCreateDialogClose = () => {
    setShowTransitionGraphCreateDialog(false);
  };

  const handleTransitionRegistrationComplete = async () => {
    showSuccess("遷移情報の登録が完了しました");
    // データを再取得（完了を待つ）
    await fetchTestTargets();
  };

  const handleResultsGenerated = (
    result: GenerateFromOperationLogResponse,
    forceFinal = false
  ) => {
    if (!result.preview || forceFinal) {
      setGeneratedResult(result);
      setShowRegistrationDialog(true);
      return;
    }

    setPendingResult(result);
    setPreviewData(result.preview || null);
    setShowPreviewDialog(true);
  };

  const handlePreviewReady = (
    preview: GenerateFromOperationLogResponse,
    request: GenerateFromOperationLogRequest
  ) => {
    setPendingResult(preview);
    setPreviewData(preview.preview || null);
    setPendingGenerateRequest(request);
    setShowPreviewDialog(true);
  };

  const handleSpecImportReady = (
    response: GenerateFromOperationLogResponse,
    _summary: SpecImportSummary,
    matchedExistingIds: Set<string>,
    deletionCandidates: DeletionCandidate[]
  ) => {
    setGeneratedResult(response);
    setShowRegistrationDialog(true);
    setSpecDeletionCandidates(deletionCandidates);

    // 既存ターゲットの親子マップを構築（削除時に子孫を葉→親順で削除するため）
    const childrenOf: Record<string, string[]> = {};
    for (const t of testTargets) {
      const parentId = t.parentId ?? t.parent_test_target_id;
      if (parentId) {
        if (!childrenOf[parentId]) childrenOf[parentId] = [];
        childrenOf[parentId].push(t.id);
      }
    }
    setSpecExistingChildrenOf(childrenOf);

    // 全既存ターゲットの id → name 逆引きマップ（削除失敗時の target_name 解決用）
    const namesById: Record<string, string> = {};
    for (const t of testTargets) {
      if (t.id) namesById[t.id] = t.name;
    }

    // streamlit を経由せず SSTF GraphQL に直接登録する handler を bind
    const handler = async (
      request: RegisterTestTargetsRequest & { deleteCandidateIds?: Set<string>; existingChildrenOf?: Record<string, string[]> }
    ): Promise<RegisterTestTargetsResponse> => {
      const result = await registerSpecImportTargets({
        resolvedTargets: request.testTargets,
        matchedExistingIds,
        deleteCandidateIds: request.deleteCandidateIds,
        existingChildrenOf: request.existingChildrenOf,
        existingNamesById: namesById,
        apiClient,
        workspaceId: request.workspaceId,
        projectId: request.projectId,
      });
      // 仕様書インポートで新規作成した service をサイドバーで自動表示 ON にする
      if (result.createdServiceIds.length > 0) {
        setSelectedServiceIds((prev) => {
          const next = new Set(prev);
          result.createdServiceIds.forEach((id) => next.add(id));
          return next;
        });
      }
      return result;
    };
    // useState の setter で関数を保存するときは関数で包む
    setSpecCustomRegisterHandler(() => handler);
  };

  const handlePreviewConfirm = async (selections: PathSelection[]) => {
    if (!apiClient || !workspaceId || !projectId) {
      showError("APIクライアントとワークスペース/プロジェクトIDを設定してください");
      return;
    }
    if (!pendingGenerateRequest) {
      showError("プレビュー結果が見つかりません");
      return;
    }

    setPathUpdateInProgress(true);
    try {
      const selectionMap = new Map<string, PathSelection>();
      selections.forEach((s) => selectionMap.set(s.path, s));

      // NOTE: 選択されたViewにpathが存在しない場合のみ更新
      for (const selection of selections) {
        const targetView =
          testTargets.find((t) => t.id === selection.viewId) ||
          testTargets.find((t) => (t as any).test_target_id === selection.viewId);
        if (!targetView) {
          // NOTE: 取得できない場合はスキップしつつ通知
          showError(`Viewが見つかりません: ${selection.viewId}`);
          continue;
        }
        const rawParams = targetView.params || {};
        const params =
          typeof rawParams === "object" ? { ...rawParams } : ({ paths: [] } as any);
        const currentPaths = Array.isArray((params as any).paths)
          ? [...((params as any).paths as any[])]
          : [];
        const hasPath = currentPaths.some(
          (p: any) => p && p.path && p.path === selection.path
        );
        if (!hasPath) {
          currentPaths.push({ path: selection.path });
          await apiClient.updateTestTarget(workspaceId, projectId, targetView.id, {
            params: { ...params, paths: currentPaths },
          });
        }
      }

      // ユーザー選択からpath_view_mappingを構築
      // NOTE: キーは「domain|path」形式（例: "https://example.com|/"）
      // NOTE: 値がviewIdの場合は既存Viewにマッチ、"__NEW_VIEW__"の場合は新規Viewとして処理
      const pathViewMapping: Record<string, string | null> = {};
      
      // selectionsに含まれるものを先に設定
      for (const selection of selections) {
        const key = `${selection.domain || ""}|${selection.path}`;
        if (selection.viewId) {
          pathViewMapping[key] = selection.viewId;
        } else {
          // viewIdがない場合は新規Viewとして処理するマーカーを設定
          pathViewMapping[key] = "__NEW_VIEW__";
        }
      }
      
      // NOTE: previewDataに含まれるがselectionsに含まれないpath_matchesは新規Viewとしてマーク
      // これにより、ユーザーが「なし」を選択したものも明示的に新規View処理される
      if (previewData?.path_matches) {
        for (const match of previewData.path_matches) {
          const key = `${match.domain || ""}|${match.path}`;
          if (!(key in pathViewMapping)) {
            pathViewMapping[key] = "__NEW_VIEW__";
          }
        }
      }

      // path更新後にLLM分析を実行（ユーザー選択を含む）
      const requestWithMapping = {
        ...pendingGenerateRequest,
        pathViewMapping,
      };
      const finalResult =
        await operationLogService.generateFromOperationLog(
          requestWithMapping
        );

      handleResultsGenerated(finalResult, true);
      setShowPreviewDialog(false);
      setPendingResult(null);
      setPreviewData(null);
      setPendingGenerateRequest(null);
    } catch (error: any) {
      showError(error?.message || "path更新中にエラーが発生しました");
    } finally {
      setPathUpdateInProgress(false);
    }
  };

  const handleRegistrationComplete = (result: any) => {
    setShowRegistrationDialog(false);
    setGeneratedResult(null);
    setSpecCustomRegisterHandler(null);
    setSpecDeletionCandidates(undefined);
    setSpecExistingChildrenOf(undefined);

    // データを再取得してUIを更新
    fetchTestTargets();
  };

  const handleImportDialogClose = () => {
    setShowImportDialog(false);
    // NOTE: selectedViewForItemsは他の用途（Itemサイドバー表示など）でも使用されるため、
    // ここではリセットしない。OperationLogImportDialog側でmodeが未指定の場合は
    // viewIdを無視するように修正済み。
  };

  const handleRegistrationDialogClose = () => {
    setShowRegistrationDialog(false);
    setGeneratedResult(null);
    setSpecCustomRegisterHandler(null);
    setSpecDeletionCandidates(undefined);
    setSpecExistingChildrenOf(undefined);
  };

  // 仕様書インポートの登録ダイアログで「戻る」を押したとき: 登録ダイアログを閉じて仕様書選択に戻る
  const handleSpecImportBack = () => {
    setShowRegistrationDialog(false);
    setGeneratedResult(null);
    setSpecCustomRegisterHandler(null);
    setSpecDeletionCandidates(undefined);
    setSpecExistingChildrenOf(undefined);
    setShowSpecImportDialog(true);
  };

  if (loading || testcaseMappingsLoading) {
    return (
      <LoadingContainer>
        <LoadingContent>
          <LoadingSpinner />
          <LoadingText>
            {apiClient && workspaceId && projectId
              ? "API経由でテストデータを読み込み中..."
              : "テストターゲットを読み込み中..."}
          </LoadingText>
        </LoadingContent>
      </LoadingContainer>
    );
  }

  const errorMessage = error || testcaseMappingsError;
  if (errorMessage) {
    // すべてのエラーで設定変更を可能にする
    // 通常のUIを表示するが、testTargetsは空配列として扱う
    const emptyServices: TestTarget[] = [];

    return (
      <AppContainer>
        <CssBaseline />
        <div style={{ display: "flex", flex: 1 }}>
          <Sidebar>
          <ServiceList
            services={emptyServices}
            selectedServiceIds={new Set()}
            testTargets={emptyServices}
            onServiceSelect={(service) => {
              setSelectedServiceIds(prev => {
                const newSet = new Set(prev);
                if (newSet.has(service.id)) {
                  newSet.delete(service.id);
                } else {
                  newSet.add(service.id);
                }
                return newSet;
              });
            }}
              apiConfig={apiConfig}
              onApiConfigChange={onApiConfigChange}
              onApiClientChange={onApiClientChange}
              workspaceId={workspaceId}
              projectId={projectId}
              onWorkspaceIdChange={onWorkspaceIdChange}
              onProjectIdChange={onProjectIdChange}
              onOperationLogImport={handleOperationLogImport}
              isProcessingOperationLog={isProcessingOperationLog}
              processingMessage={processingMessage}
              onTransitionGraphImport={handleTransitionGraphImport}
              onTransitionGraphCreate={handleTransitionGraphCreate}
              showApiConfigDialog={showApiConfigDialog}
              onApiConfigComplete={onApiConfigComplete}
              width={serviceListWidth}
              onWidthChange={setServiceListWidth}
              onCreateService={handleCreateService}
              onAddChild={handleAddChild}
            />
          </Sidebar>
          <MainContent>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                flexDirection: "column",
                textAlign: "center",
                padding: "40px",
              }}
            >
              <div
                style={{
                  background: "white",
                  padding: "40px",
                  borderRadius: "16px",
                  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
                  borderLeft: "4px solid #f44336",
                }}
              >
                <div style={{ fontSize: "3rem", marginBottom: "16px" }}>⚠️</div>
                <div
                  style={{
                    color: "#d32f2f",
                    fontSize: "1.1rem",
                    fontWeight: 600,
                    marginBottom: "8px",
                  }}
                >
                  {errorMessage.includes("認証が無効") ||
                  errorMessage.includes("トークンを再設定")
                    ? "認証エラーが発生しました"
                    : "読み込みエラーが発生しました"}
                </div>
                <div
                  style={{
                    fontSize: "0.9rem",
                    opacity: 0.8,
                    marginBottom: "16px",
                  }}
                >
                  {errorMessage}
                </div>
                <div style={{ fontSize: "0.9rem", color: "#666" }}>
                  左側のサイドバーにある「API設定」ボタンをクリックして、
                  <br />
                  {errorMessage.includes("認証が無効") ||
                  errorMessage.includes("トークンを再設定")
                    ? "トークンを再設定してください。"
                    : "API設定を確認・変更してください。"}
                </div>
              </div>
            </div>
          </MainContent>
        </div>
      </AppContainer>
    );
  }

  return (
    <AppContainer>
      <CssBaseline />
      <div style={{ display: "flex", flex: 1 }}>
        <Sidebar>
          <ServiceList
            services={services}
            selectedServiceIds={selectedServiceIds}
            testTargets={testTargets}
            onServiceSelect={(service) => {
              setSelectedServiceIds(prev => {
                const newSet = new Set(prev);
                if (newSet.has(service.id)) {
                  newSet.delete(service.id);
                } else {
                  newSet.add(service.id);
                }
                return newSet;
              });
            }}
            onViewSelect={handleViewSelect}
            onOpenServiceDetail={(service) => setRequestedServiceForDetail(service)}
            apiConfig={apiConfig}
            onApiConfigChange={onApiConfigChange}
            onApiClientChange={onApiClientChange}
            workspaceId={workspaceId}
            projectId={projectId}
            onWorkspaceIdChange={onWorkspaceIdChange}
            onProjectIdChange={onProjectIdChange}
            onOperationLogImport={handleOperationLogImport}
            onLoadGenerationResultJson={
              isGenerationResultExportImportEnabled()
                ? handleLoadGenerationResultJson
                : undefined
            }
            isProcessingOperationLog={isProcessingOperationLog}
            processingMessage={processingMessage}
            onTransitionGraphImport={handleTransitionGraphImport}
            onTransitionGraphCreate={handleTransitionGraphCreate}
            onSpecImportReady={handleSpecImportReady}
            specImportDialogOpen={showSpecImportDialog}
            onSpecImportDialogOpenChange={setShowSpecImportDialog}
            showApiConfigDialog={showApiConfigDialog}
            onApiConfigComplete={onApiConfigComplete}
            width={serviceListWidth}
            onWidthChange={setServiceListWidth}
            onCreateService={handleCreateService}
            onAddChild={handleAddChild}
          />
        </Sidebar>
        <MainContent>
          <TestTargetGraphView
            services={services}
            selectedServiceIds={selectedServiceIds}
            onSelectedServiceIdsChange={setSelectedServiceIds}
            testTargets={testTargets}
            testcaseMappings={testcaseMappings}
            loading={loading || testcaseMappingsLoading}
            onRefresh={fetchTestTargets}
            onUpdateTestTarget={updateTestTarget}
            onAddTestTarget={addTestTarget}
            onMoveTarget={handleMoveTarget}
            onAddChild={handleAddChild}
            onDeleteTarget={handleDeleteTarget}
            apiClient={apiClient}
            apiConfig={apiConfig}
            workspaceId={workspaceId}
            projectId={projectId}
            workspaceName={workspaceName}
            projectName={projectName}
            selectedViewForItems={selectedViewForItems}
            selectedViewSource={selectedViewSource}
            onViewPanelClose={handleViewPanelClose}
            onViewSelect={handleViewSelect}
            operationLogService={operationLogService}
            onOpenItemDetail={handleOpenItemDetail}
            requestedServiceForDetail={requestedServiceForDetail}
            onOpenServiceDetailHandled={() => setRequestedServiceForDetail(null)}
            urlTargetId={urlTargetId}
            urlUsecaseId={urlUsecaseId}
          urlEditMode={urlEditMode}
            onCloseDialog={onCloseDialog}
          />
        </MainContent>
      </div>

      <TestTargetDetailView
        open={isFormOpen}
        onClose={handleFormClose}
        onSave={handleFormSubmit}
        testTargets={testTargets}
        testTarget={
          formData
            ? ({
                ...formData,
                id: (formData as any).id || "",
                metaInfo: (formData as any).metaInfo || {
                  createdAt: "",
                  createdBy: "",
                  modifiedAt: "",
                  modifiedBy: "",
                },
              } as TestTarget)
            : null
        }
        initialMode={isCreateMode ? "create" : "edit"}
        initialTab={detailViewInitialTab}
        testcaseMappings={testcaseMappings}
      />

      <input
        type="file"
        ref={generationResultFileInputRef}
        accept=".json,application/json"
        onChange={handleGenerationResultFileChange}
        style={{ display: "none" }}
        aria-hidden
      />
      {/* 操作ログインポートダイアログ */}
      <OperationLogImportDialog
        open={showImportDialog}
        onClose={handleImportDialogClose}
        onResultsGenerated={handleResultsGenerated}
        onPreviewReady={handlePreviewReady}
        workspaceId={workspaceId}
        projectId={projectId}
        operationLogService={operationLogService}
        viewId={selectedViewForItems?.id}
        viewName={selectedViewForItems?.name}
      />

      <GroupPreviewDialog
        open={showPreviewDialog}
        preview={previewData}
        services={testTargets.filter((t) => t.type.toLowerCase() === "service")}
        views={testTargets.filter(
          (t) =>
            t.type.toLowerCase() === "view" ||
            t.type.toLowerCase() === "component"
        )}
        onClose={() => {
          setShowPreviewDialog(false);
          setPendingResult(null);
          setPreviewData(null);
        }}
        onConfirm={handlePreviewConfirm}
        processing={pathUpdateInProgress}
      />

      {/* 遷移情報一括登録ダイアログ */}
      <TransitionGraphDialog
        open={showTransitionGraphDialog}
        onClose={handleTransitionGraphDialogClose}
        onRegistrationComplete={handleTransitionRegistrationComplete}
        workspaceId={workspaceId}
        projectId={projectId}
        transitionGraphService={transitionGraphService}
        apiClient={apiClient}
      />

      {/* 操作ログから遷移情報のGraph作成ダイアログ */}
      <TransitionGraphCreateDialog
        open={showTransitionGraphCreateDialog}
        onClose={handleTransitionGraphCreateDialogClose}
        workspaceId={workspaceId}
        projectId={projectId}
      />

      {/* テストターゲット登録ダイアログ */}
      <TestTargetRegistrationDialog
        open={showRegistrationDialog}
        onClose={handleRegistrationDialogClose}
        onRegistrationComplete={handleRegistrationComplete}
        targets={generatedResult?.page_results || []}
        existingTargets={testTargets}
        workspaceId={workspaceId}
        projectId={projectId}
        operationLogService={operationLogService}
        traceId={generatedResult?.trace_id}
        traceparent={generatedResult?.traceparent}
        generationNote={generatedResult?.generation_note}
        generationNoteLinkUrl={generatedResult?.generation_note_link_url}
        // 仕様書インポートの登録経路スイッチ: 本行を有効 = 直 GraphQL 経路、
        // 本行のみコメントアウト = streamlit /register backend 経由 (operationLogService.registerTestTargets)。
        // viewMode / hideUsecasesTab は specCustomRegisterHandler 由来のため、本行のみ切替で階層プレビューは維持される。
        customRegisterHandler={specCustomRegisterHandler ?? undefined}
        viewMode={specCustomRegisterHandler ? "hierarchical" : "flat"}
        hideUsecasesTab={!!specCustomRegisterHandler}
        deletionCandidates={specDeletionCandidates}
        existingChildrenOf={specExistingChildrenOf}
        onBack={specCustomRegisterHandler ? handleSpecImportBack : undefined}
      />
    </AppContainer>
  );
};

export default TestTargetManager;
